import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import CompanyDetailsModal from '../components/modals/CompanyDetailsModal';
import API_BASE_URL from '../config/apiConfig.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';


function CompanyList({ onAddCompany, onEditCompany, onViewProfile }) {
    const { hasPermission, user } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = sessionStorage.getItem('lastPage_CompanyList');
        return savedPage ? parseInt(savedPage) : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    const fetchPlans = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/plan`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.plans && Array.isArray(data.plans)) {
                    setPlans(data.plans);
                } else if (Array.isArray(data)) {
                    setPlans(data);
                }
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const userRole = user?.role || JSON.parse(sessionStorage.getItem('user'))?.role;
    const isSuperAdmin = userRole === 'SUPER_ADMIN';
    const isShreeja = (user?.companyId === null || user?.company_id === null) && !isSuperAdmin;

    // Fetch Companies from API
    const fetchCompanies = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const token = sessionStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);
            queryParams.append('page', page);
            queryParams.append('limit', limit);

            const response = await fetch(`${API_BASE_URL}/company?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.companies && Array.isArray(data.companies)) {
                    setRows(data.companies);
                    setTotalEntries(data.total || 0);
                    setTotalPages(data.pages || 1);
                } else if (Array.isArray(data)) {
                    setRows(data);
                    setTotalEntries(data.length);
                    setTotalPages(Math.ceil(data.length / limit));
                }
            } else {
                const err = await response.json();
                toast.error(err.message || 'Failed to fetch companies');
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast.error('Server error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        sessionStorage.setItem('lastPage_CompanyList', currentPage);
    }, [currentPage]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCompanies(searchQuery, currentPage, itemsPerPage);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handleView = (company) => {
        setSelectedCompany(company);
        setIsViewModalOpen(true);
    };

    const handleEdit = (company) => {
        if (onEditCompany) {
            onEditCompany(company);
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;

            // Fetch the count of users for this company
            const countResponse = await fetch(`${API_BASE_URL}/users?company_id=${id}&limit=1`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            let userCount = 0;
            if (countResponse.ok) {
                const countData = await countResponse.json();
                userCount = countData.total || 0;
            }

            let confirmText = 'Do you want to delete this company?';
            if (userCount > 0) {
                confirmText = `This company has ${userCount} user${userCount === 1 ? '' : 's'}. Deleting this company will also delete all of its users. Do you want to proceed?`;
            }

            Swal.fire({
                title: 'Are you sure?',
                text: confirmText,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const response = await fetch(`${API_BASE_URL}/company/${id}`, {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });

                        if (response.ok) {
                            Swal.fire(
                                'Deleted!',
                                'Company and all associated records have been deleted.',
                                'success'
                            );
                            // Refresh Data
                            fetchCompanies(searchQuery, currentPage, itemsPerPage);
                        } else {
                            const err = await response.json();
                            toast.error(err.message || 'Failed to delete company');
                        }
                    } catch (error) {
                        console.error('Error deleting company:', error);
                        toast.error('Server error');
                    }
                }
            });
        } catch (error) {
            console.error('Error fetching user count:', error);
            toast.error('Error fetching company details');
        }
    };

        const handleAdd = () => {
        if (onAddCompany) {
            onAddCompany();
        }
    };

    const handleExcelDownload = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0'); // Get all companies

            const response = await fetch(`${API_BASE_URL}/company?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const companiesToExport = data.companies || (Array.isArray(data) ? data : []);

                const worksheetData = companiesToExport.map((r, idx) => ({
                    'No.': idx + 1,
                    'Company Name': r.companyName || '',
                    'Person Name': r.companyPersonName || '',
                    'Email': r.companyEmail || '',
                    'Mobile Number': r.companyMobileNumber_1 || '',
                    'GST Number': r.companyGstNumber || '',
                    'Plan': r.planName || '',
                    'Expiry Date': r.planExpiryDate ? new Date(r.planExpiryDate).toLocaleDateString('en-GB') : ''
                }));

                const worksheet = XLSX.utils.json_to_sheet(worksheetData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Companies');
                XLSX.writeFile(workbook, 'CompanyList.xlsx');
            } else {
                toast.error('Failed to fetch data for Excel export');
            }
        } catch (error) {
            console.error("Excel download error:", error);
            toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0'); // Get all companies

            const response = await fetch(`${API_BASE_URL}/company?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const companiesToPrint = data.companies || (Array.isArray(data) ? data : []);

                const printWindow = window.open('', '', 'height=600,width=800');
                printWindow.document.write('<html><head><title>Company List</title>');
                printWindow.document.write('<style>');
                printWindow.document.write('@page { size: A4 portrait; margin: 10mm; }');
                printWindow.document.write('body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }');
                printWindow.document.write('h1 { text-align: center; color: #0b3a54; font-size: 16px; margin-bottom: 10px; }');
                printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 5px; table-layout: auto; }');
                printWindow.document.write('th, td { border: 1px solid #ccc; padding: 4px 2px; text-align: left; font-size: 8px; word-break: break-word; }');
                printWindow.document.write('th { background-color: #f0fdf4; color: #0b3a54; font-weight: 700; text-transform: uppercase; }');
                printWindow.document.write('</style></head><body>');
                printWindow.document.write('<h1>Company List</h1>');
                printWindow.document.write('<table><thead><tr>');
                printWindow.document.write('<th>No.</th>');
                printWindow.document.write('<th>Company Name</th>');
                printWindow.document.write('<th>Person Name</th>');
                printWindow.document.write('<th>Email</th>');
                printWindow.document.write('<th>Mobile Number</th>');
                printWindow.document.write('<th>GST Number</th>');
                printWindow.document.write('<th>Plan</th>');
                printWindow.document.write('<th>Expiry Date</th>');
                printWindow.document.write('</tr></thead><tbody>');

                companiesToPrint.forEach((r, index) => {
                    const expiryStr = r.planExpiryDate ? new Date(r.planExpiryDate).toLocaleDateString('en-GB') : '-';
                    printWindow.document.write('<tr>');
                    printWindow.document.write(`<td>${index + 1}</td>`);
                    printWindow.document.write(`<td>${r.companyName || ''}</td>`);
                    printWindow.document.write(`<td>${r.companyPersonName || ''}</td>`);
                    printWindow.document.write(`<td>${r.companyEmail || ''}</td>`);
                    printWindow.document.write(`<td>${r.companyMobileNumber_1 || ''}</td>`);
                    printWindow.document.write(`<td>${r.companyGstNumber || ''}</td>`);
                    printWindow.document.write(`<td>${r.planName || '-'}</td>`);
                    printWindow.document.write(`<td>${expiryStr}</td>`);
                    printWindow.document.write('</tr>');
                });

                printWindow.document.write('</tbody></table></body></html>');
                printWindow.document.close();
                printWindow.print();
            } else {
                toast.error('Failed to fetch data for printing');
            }
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    // Pagination Display Logic
    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        {
            key: 'companyName',
            label: 'NAME',
            render: (item) => (
                <span
                    onClick={() => onViewProfile && onViewProfile(item)}
                    style={{
                        color: '#2563eb',
                        cursor: 'pointer',
                        fontWeight: '600'
                    }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                    {item.companyName}
                </span>
            )
        },
        { key: 'companyPersonName', label: 'PERSON NAME' },
        { key: 'companyEmail', label: 'EMAIL' },
        { key: 'companyMobileNumber_1', label: 'MOBILE NUMBER' },
        { key: 'companyGstNumber', label: 'GST' },
        {
            key: 'isActive',
            label: 'STATUS',
            render: (item) => {
                const handleToggle = async (e) => {
                    e.stopPropagation();
                    const newStatus = !item.isActive;
                    const confirmText = `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this company?`;
                    const result = await Swal.fire({
                        title: 'Change Status?',
                        text: confirmText,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: `Yes, ${newStatus ? 'Activate' : 'Deactivate'}`
                    });
                    if (result.isConfirmed) {
                        try {
                            const token = sessionStorage.getItem('token');
                            const response = await fetch(`${API_BASE_URL}/company/${item._id || item.id}`, {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ isActive: newStatus })
                            });
                            if (response.ok) {
                                toast.success(`Company ${newStatus ? 'activated' : 'deactivated'} successfully`);
                                fetchCompanies(searchQuery, currentPage, itemsPerPage);
                            } else {
                                const err = await response.json();
                                toast.error(err.message || 'Failed to update company status');
                            }
                        } catch (error) {
                            console.error('Error toggling status:', error);
                            toast.error('Server error');
                        }
                    }
                };

                return (
                    <span 
                        onClick={handleToggle}
                        style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: item.isActive ? '#dcfce7' : '#fee2e2',
                            color: item.isActive ? '#16a34a' : '#dc2626',
                            cursor: 'pointer'
                        }}
                        title="Click to toggle status"
                    >
                        {item.isActive ? 'Active' : 'Deactive'}
                    </span>
                );
            }
        },
        { key: 'planName', label: 'PLAN', render: (item) => item.planName || <span style={{ color: '#94a3b8', fontSize: '12px' }}>No Plan</span> },
        {
            key: 'planExpiryDate',
            label: 'PLAN EXPIRY',
            render: (item) => {
                if (!item.planExpiryDate) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;
                const now = new Date();
                const expiry = new Date(item.planExpiryDate);
                const diffDays = Math.floor((expiry.setHours(0,0,0,0) - now.setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
                const isExpired = diffDays <= 0;
                const isWarn = !isExpired && diffDays <= 10;
                return (
                    <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: isExpired ? '#fee2e2' : isWarn ? '#fef3c7' : '#dcfce7',
                        color: isExpired ? '#dc2626' : isWarn ? '#d97706' : '#16a34a'
                    }}>
                        {isExpired ? `Expired` : diffDays === 1 ? `Last day` : `${diffDays}d left`}
                    </span>
                );
            }
        },
        {
            key: 'actions',
            label: 'ACTIONS',
            render: (item) => {
                let showWhatsApp = false;
                let diffDays = 0;
                const hasNoPlan = !item.planName;
                if (item.planExpiryDate) {
                    const now = new Date();
                    const expiry = new Date(item.planExpiryDate);
                    diffDays = Math.floor((expiry.setHours(0,0,0,0) - now.setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
                    showWhatsApp = diffDays <= 10;
                }
                if (hasNoPlan) {
                    showWhatsApp = true;
                }
                const handleWhatsAppClick = () => {
                    const mobile = item.companyMobileNumber_1 || item.companyMobileNumber_2 || item.company_mobile_1 || item.company_mobile_2;
                    if (!mobile) {
                        toast.warning('No mobile number available for this company');
                        return;
                    }
                    const cleanDigits = mobile.replace(/\D/g, '');
                    const phone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                    const companyName = item.companyName || item.company_name || 'Valued Customer';
                    
                    let introText = '';
                    if (hasNoPlan) {
                        introText = `Hello *${companyName}*,\n\nPlease select a plan for your account to get started.`;
                    } else if (diffDays <= 0) {
                        introText = `Hello *${companyName}*,\n\nYour subscription for *${companyName}* is expired. Please renew your plan.`;
                    } else {
                        const remainingText = diffDays === 1 ? '1 day' : `${diffDays} days`;
                        introText = `Hello *${companyName}*,\n\nYour subscription for *${companyName}* has *${remainingText}* remaining. After that, your account will be deactivated. Please renew your plan.`;
                    }

                    let oldPlanDetails = `*Your Old Plan Details:*\n` +
                        `- Plan Name: ${item.planName || 'No Plan'}\n`;
                    if (item.planStartDate) {
                        oldPlanDetails += `- Start Date: ${new Date(item.planStartDate).toLocaleDateString('en-IN')}\n`;
                    }
                    if (item.planExpiryDate) {
                        oldPlanDetails += `- Expiry Date: ${new Date(item.planExpiryDate).toLocaleDateString('en-IN')}\n`;
                    }

                    let availablePlansText = '';
                    if (plans && plans.length > 0) {
                        availablePlansText = `*Available Plans to Select or Renew:*\n` +
                            plans.map(p => `- *${p.planName}* (${p.planDurationDays} Days) - Price: ₹${p.finalPrice || p.planPrice || 0}`).join('\n') + `\n`;
                    }

                    const messageText = `${introText}\n\n${oldPlanDetails}\n${availablePlansText}\nPlease let us know which plan you would like to select or renew.`;
                    const message = encodeURIComponent(messageText);
                    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                };

                return (
                    <div className="list-page-action-icons">
                        {showWhatsApp && (
                            <button
                                type="button"
                                className="list-page-icon-btn"
                                title={hasNoPlan ? "Send Plan Request on WhatsApp" : "Send Expiry Alert on WhatsApp"}
                                onClick={handleWhatsAppClick}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
                                </svg>
                            </button>
                        )}
                        {hasPermission('Company', 'view') && (isSuperAdmin || isShreeja) && (
                            <button
                                type="button"
                                className="list-page-icon-btn"
                                title="View"
                                onClick={() => handleView(item)}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            </button>
                        )}
                        {hasPermission('Company', 'update') && (
                            <button
                                type="button"
                                className="list-page-icon-btn"
                                title="Edit"
                                onClick={() => handleEdit(item)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                            </button>
                        )}
                        {hasPermission('Company', 'delete') && (
                            <button
                                type="button"
                                className="list-page-icon-btn"
                                title="Delete"
                                onClick={() => handleDelete(item._id)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="list-page-container">
            {/* Header: Title and Action Buttons */}
                        <div className="list-page-header">
                <h1 className="list-page-title">Company List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('Company', 'report') && (
                        <>
                            <button className="list-page-btn list-page-btn-excel" onClick={handleExcelDownload}>
                                EXCEL
                            </button>
                            <button className="list-page-btn list-page-btn-print" onClick={handlePrint}>
                                PRINT
                            </button>
                        </>
                    )}
                    {hasPermission('Company', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>
                            ADD COMPANY
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                searchBar={
                    <SearchBar
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search companies..."
                    />
                }
            />

            {/* View Company Details Modal */}
            <CompanyDetailsModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                company={selectedCompany}
            />
        </div>
    );
}

export default CompanyList;
